import { Empty, Spin, Skeleton } from "antd";
import { MessageOutlined } from "@ant-design/icons";
import { ConversationItem } from "../../ConversationItem";
import type { Conversation } from "@interfaces/chat.interface";
import type { Agent } from "@interfaces/api.interface";
import classNames from "classnames";
import styles from "./styles.module.less";

interface ConversationItemsProps {
  conversations: Conversation[];
  isLoading: boolean;
  isLoadingMore: boolean;
  showEmptyState: boolean;
  searchValue: string;
  selectedFanpageName?: string;
  selectedConversationId?: string;
  hasMore: boolean;
  conversationTagsMap: Record<
    string,
    Array<{ description: string; id: number; tagName: string }>
  >;
  conversationFolderStatusMap: { folder?: string; externalId: string }[];
  agentsByConversation: Record<string, Agent>;
  onSelectConversation: (conversation: Conversation) => void;
  onPin: (conversationId: string) => void;
  onMute: (conversationId: string) => void;
  onDelete: (conversationId: string) => void;
  onOpenFanpageMenu?: () => void;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  loadMoreSentinelRef: (node: HTMLDivElement | null) => void;
  isSyncing?: boolean;
  movedConversationInfo?: { conversationId: string; targetTabLabel: string } | null;
}

const EmptyPage = ({
  showEmptyState,
  searchValue,
  selectedFanpageName,
}: {
  showEmptyState: boolean;
  searchValue: string;
  selectedFanpageName: string;
}) => {
  return (
    <div
      className={classNames(styles.emptyState, {
        [styles.fadeIn]: showEmptyState,
      })}
      style={{ opacity: 1 }}
    >
      <Empty
        image={
          <MessageOutlined
            className={styles.emptyIcon}
            style={{ fontSize: 80 }}
          />
        }
        description={
          <div className={styles.emptyDescription}>
            <p className={styles.emptyTitle}>Chưa có hội thoại nào</p>
            <p className={styles.emptySubtitle}>
              {searchValue
                ? "Không tìm thấy hội thoại phù hợp với từ khóa của bạn"
                : selectedFanpageName
                ? `Chưa có hội thoại nào cho fanpage "${selectedFanpageName}"`
                : "Hãy chọn một fanpage để xem các hội thoại"}
            </p>
          </div>
        }
      />
    </div>
  );
};


export const ConversationItems = ({
  conversations,
  isLoading,
  isLoadingMore,
  showEmptyState,
  searchValue,
  selectedFanpageName,
  selectedConversationId,
  hasMore,
  conversationTagsMap,
  conversationFolderStatusMap,
  agentsByConversation,
  onSelectConversation,
  onPin,
  onMute,
  onDelete,
  onOpenFanpageMenu,
  scrollContainerRef,
  loadMoreSentinelRef,
  isSyncing = false,
  movedConversationInfo,
}: ConversationItemsProps) => {
  return (
    <div
      ref={scrollContainerRef}
      className={classNames(styles.items, "scroll-thin", {
        [styles.disabled]: isSyncing,
      })}
    >
      {isLoading ? (
        <div className={styles.loadingState}>
          <Spin size="large" />
          <div className={styles.loadingSkeletons}>
            {[1, 2, 3].map((i) => (
              <div key={i} className={styles.skeletonItem}>
                <Skeleton avatar active paragraph={{ rows: 2 }} />
              </div>
            ))}
          </div>
        </div>
      ) : conversations.length > 0 ? (
        <>
          {conversations.map((conversation) => {
            const conversationId = conversation.lastMessage?.conversationId;
            const tags = conversationId
              ? conversationTagsMap[conversationId] || []
              : [];
            const agent = conversationId
              ? agentsByConversation[conversationId]
              : undefined;
            const folderStatus = conversationId
              ? conversationFolderStatusMap?.find((item: { externalId: string }) => item.externalId === conversationId)?.folder || ""
              : undefined;
            const conversationExternalId = conversation.lastMessage?.conversationId || conversation.id;
            const isMovedConversation = movedConversationInfo?.conversationId === conversationExternalId;
            
            return (
              <div
                key={conversation.id}
                className={styles.conversationItemWrapper}
              >
                <ConversationItem
                  conversation={conversation}
                  isActive={conversation.id === selectedConversationId}
                  onSelect={() => !isSyncing && onSelectConversation(conversation)}
                  onPin={() => !isSyncing && onPin(conversation.id)}
                  onMute={() => !isSyncing && onMute(conversation.id)}
                  onDelete={() => !isSyncing && onDelete(conversation.id)}
                  onOpenFanpageMenu={onOpenFanpageMenu}
                  tags={tags}
                  agent={agent}
                  searchValue={searchValue}
                  folderStatus={folderStatus}
                  movedToTab={isMovedConversation ? movedConversationInfo?.targetTabLabel : undefined}
                />
              </div>
            );
          })}
          {!searchValue && (
            <div
              ref={loadMoreSentinelRef}
              className={styles.loadMoreSentinel}
              style={{
                minHeight: hasMore ? "50px" : "1px",
                visibility: hasMore ? "visible" : "hidden",
              }}
            >
              {isLoadingMore && (
                <div className={styles.loadingMoreIndicator}>
                  <Spin size="small" />
                  <span>Đang tải thêm...</span>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <EmptyPage
          showEmptyState={showEmptyState}
          searchValue={searchValue}
          selectedFanpageName={selectedFanpageName || ""}
        />
      )}
    </div>
  );
};
